import { router, usePage } from "@inertiajs/react"
import {
  ArrowRight,
  type LucideIcon,
  ShieldCheck,
  Sparkles,
  TrendingUp,
  Zap,
} from "lucide-react"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { RankBlazon } from "@/components/rank/rank-blazon"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog"
import { useCurrentContext } from "@/hooks/use-current-context"
import { usePrefersReducedMotion } from "@/hooks/use-prefers-reduced-motion"
import { fireRankUpConfetti } from "@/lib/confetti"
import { cn } from "@/lib/utils"
import { creatorProfilePath, creatorProfileRankSeenPath } from "@/routes"
import type { PendingRankUp } from "@/types/rank"

interface RankUpModalSharedSlice {
  pending_rank_up?: PendingRankUp | null
  // Index signature satisfies the `usePage<T extends PageProps>` constraint.
  [key: string]: unknown
}

// Decorative Lucide icons cycled across the privilege rows (UX Spec §2 —
// icons are `aria-hidden`; the perk TEXT conveys meaning, never the icon).
const PRIVILEGE_ICONS: readonly LucideIcon[] = [
  Zap,
  TrendingUp,
  ShieldCheck,
  Sparkles,
]

/**
 * OKS-543 — Full-screen rank-up celebration (evolves the OKS-397 centered
 * dialog; replaces the OKS-49 Sonner toast).
 *
 * Mounted in `creator-shell.tsx` so every creator page render gets the
 * shared-prop pickup path. Subscribes to `usePage().props.pending_rank_up`
 * — the BE emits the single highest unobserved rank-up on the current
 * membership (display metadata only), or `null`. Real-time refresh arrives
 * via `useRealtimeRefetch` (Solid Cable, OKS-406); the next-render pull is
 * the fallback.
 *
 * # Open / dismiss
 *
 * Opens when a non-null `pending_rank_up` arrives whose rank name hasn't
 * already been dismissed this session (`dismissedName` — the name flipping
 * signals a NEW crossing). EVERY close path — the two-act "Continue", "View
 * profile", the X, backdrop, Esc — routes through `dismiss()`, which
 * optimistically closes and fires `PATCH /profile/rank_seen`. The close is
 * optimistic: if the PATCH fails the rank-up stays unobserved server-side
 * and the modal simply reappears on the next render — idempotent, no
 * blocking error UI (UX Spec §3 Error).
 *
 * # Two acts
 *
 * The celebration body ({@link RankUpCelebration}) is keyed on the rank
 * name, so each new crossing mounts a fresh instance at the **Peak** act
 * and fires exactly one confetti burst. Peak's Continue advances to the
 * **Privileges** ("YOU'VE UNLOCKED") act when the rank has privileges, or
 * dismisses directly when it has none. Privileges' Continue dismisses.
 *
 * # Multi-tier jump
 *
 * The BE returns only the highest crossed tier, so this renders exactly ONE
 * celebration even when several events were persisted (UX Spec §1 edge).
 */
export function RankUpModal() {
  const pending = usePage<RankUpModalSharedSlice>().props.pending_rank_up

  // `open` is DERIVED (no setState-in-effect): open whenever a non-null
  // `pending_rank_up` is present whose name we haven't dismissed. A NEW
  // crossing (different name) re-opens automatically; the same name after
  // dismissal stays closed — mirrors the old toast's by-name de-dup.
  const [dismissedName, setDismissedName] = useState<string | null>(null)
  const open = pending != null && pending.name !== dismissedName

  // Marks the rank-up seen. `onFinish` (optional) is threaded straight
  // into the mark-seen visit's options so a caller can CHAIN a follow-up
  // navigation onto it (see `handleViewProfile`). It is deliberately
  // `onFinish` — not `onSuccess` — so the callback runs on success,
  // error, AND cancel: a failed/offline mark-seen must never strand the
  // caller (OKS-671, mirroring OKS-642). The dismiss-in-place callers
  // (X / backdrop / Esc / Peak- & Privileges-Continue) pass no callback
  // and are unaffected.
  const dismiss = (options: { onFinish?: () => void } = {}) => {
    if (pending) setDismissedName(pending.name)
    router.patch(
      creatorProfileRankSeenPath(),
      {},
      {
        preserveScroll: true,
        preserveState: true,
        // No-op `only` keeps the dismissal response minimal; the
        // `pending_rank_up` prop clears on the next full visit.
        only: [],
        onFinish: options.onFinish,
      },
    )
  }

  const handleOpenChange = (next: boolean) => {
    // Radix fires this for X / backdrop / Esc. Closing always dismisses.
    if (!next) dismiss()
  }

  // Empty / not-triggered state: nothing rendered (no overlay, no DOM).
  // `pending` stays non-null through the close-out animation, so the
  // celebration content remains stable as it animates out.
  if (!pending) return null

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        data-testid="rank-up-modal"
        // Full-screen takeover: edge-to-edge 100dvh column on mobile
        // (UX Spec §5), capped to a centered ~420px card on a dimmed
        // backdrop at ≥sm so it doesn't read oddly on wide screens.
        className={cn(
          "top-0 left-0 flex h-[100dvh] max-w-none translate-x-0 translate-y-0 flex-col gap-0 rounded-none border-0 p-0",
          "sm:top-[50%] sm:left-[50%] sm:h-auto sm:max-h-[90dvh] sm:max-w-md sm:translate-x-[-50%] sm:translate-y-[-50%] sm:rounded-2xl sm:border",
        )}
      >
        {/* Keyed on the rank name so a genuinely new crossing remounts at
            the Peak act + re-fires the single confetti burst. */}
        <RankUpCelebration
          key={pending.name}
          pending={pending}
          onDismiss={dismiss}
        />
      </DialogContent>
    </Dialog>
  )
}

type Act = "peak" | "privileges"

interface RankUpCelebrationProps {
  pending: PendingRankUp
  // Accepts optional `{ onFinish }` so "View profile" can chain its
  // navigation onto the mark-seen PATCH's settle (OKS-671). No-arg calls
  // (Peak / Privileges Continue) dismiss in place, unchanged.
  onDismiss: (options?: { onFinish?: () => void }) => void
}

/**
 * The celebration body — a two-act state machine on a single full-screen
 * surface. Mounted fresh per crossing (keyed by rank name in the parent),
 * so `act` resets to `"peak"` and the confetti effect fires exactly once
 * without any setState-in-effect for the act.
 */
function RankUpCelebration({ pending, onDismiss }: RankUpCelebrationProps) {
  const { t } = useTranslation()
  const { currentBrand } = useCurrentContext()
  const reducedMotion = usePrefersReducedMotion()

  const [act, setAct] = useState<Act>("peak")
  const hasPrivileges = pending.privileges.length > 0

  // Confetti fires ONCE on Peak reveal (mount). The COMPONENT is the single
  // reduced-motion gate (source of truth for the spec spy): under reduced
  // motion we simply never invoke the wrapper. Fires only on the initial
  // mount — never on the act change (UX Spec §4: no repeat).
  useEffect(() => {
    if (reducedMotion) return
    fireRankUpConfetti()
    // Intentionally mount-only: the celebration remounts per crossing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handlePeakContinue = () => {
    if (hasPrivileges) {
      setAct("privileges")
    } else {
      onDismiss()
    }
  }

  // OKS-671 — the "View profile" CTA must reliably land the creator on
  // their profile. The old shape fired the mark-seen PATCH (via
  // `onDismiss()`) and `router.visit(profile)` CONCURRENTLY on the single
  // Inertia router, which cancels one against the other → the navigation
  // was intermittently dropped and the modal stayed stuck. Fix (mirrors
  // OKS-642's `handleSeeAll`): CHAIN the navigation onto the mark-seen
  // visit's `onFinish`, so exactly one visit is ever in flight and the
  // navigation always runs once the PATCH settles (success, error, or
  // cancel — navigation is never blocked by a mark-seen failure).
  // `onDismiss` still closes the modal optimistically first, so it never
  // visibly lingers.
  const handleViewProfile = () => {
    onDismiss({ onFinish: () => router.visit(creatorProfilePath()) })
  }

  const motionSafe = (className: string) =>
    reducedMotion ? undefined : className

  return (
    <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-6 px-6 pt-10 pb-[max(1.5rem,env(safe-area-inset-bottom))] text-center sm:pt-8">
      {act === "peak" ? (
        <>
          <div className="flex flex-1 flex-col items-center justify-center gap-4">
            {/* Crest — the newly-reached rank's blazon at hero size.
                Decorative (the title names the rank). Scale-in + glow are
                motion-safe. */}
            <span className="relative inline-flex items-center justify-center">
              <span
                aria-hidden="true"
                className={cn(
                  "bg-primary/25 absolute inset-0 -z-10 rounded-full blur-2xl",
                  motionSafe("motion-safe:animate-rank-settle"),
                )}
              />
              <span className={motionSafe("motion-safe:animate-rank-pop")}>
                <RankBlazon
                  input={{
                    name: pending.name,
                    tier: pending.tier,
                    tierCount: pending.tier_count,
                    imageUrl: pending.image_url,
                  }}
                  size="hero"
                  decorative
                />
              </span>
            </span>

            <span
              aria-hidden="true"
              className="text-primary text-xs font-semibold tracking-[0.25em] uppercase"
            >
              {t("rank.modal.eyebrow")}
            </span>

            <DialogTitle
              aria-label={t("rank.modal.title_aria", { name: pending.name })}
              className="text-3xl leading-tight font-bold text-balance"
            >
              {t("rank.modal.heading", { name: pending.name })}
            </DialogTitle>

            {pending.previous_rank_name && (
              <span
                data-testid="rank-up-transition"
                className="text-muted-foreground flex max-w-full items-center justify-center gap-2 text-sm"
                aria-label={t("rank.modal.prev_new_aria", {
                  prev: pending.previous_rank_name,
                  next: pending.name,
                })}
              >
                <span className="max-w-[8rem] truncate">
                  {pending.previous_rank_name}
                </span>
                <ArrowRight aria-hidden="true" className="size-4 shrink-0" />
                <span className="text-foreground max-w-[8rem] truncate font-medium">
                  {pending.name}
                </span>
              </span>
            )}

            {currentBrand?.name && (
              <DialogDescription className="text-balance">
                {t("rank.modal.trust_subline", { brand: currentBrand.name })}
              </DialogDescription>
            )}
          </div>

          <div className="flex w-full flex-col gap-2">
            <Button
              data-testid="rank-up-modal-continue"
              className="w-full"
              onClick={handlePeakContinue}
            >
              {t("rank.modal.cta_continue")}
            </Button>
            <Button
              variant="ghost"
              data-testid="rank-up-modal-view-profile"
              className="w-full"
              onClick={handleViewProfile}
            >
              {t("rank.modal.cta_view_profile")}
            </Button>
          </div>
        </>
      ) : (
        <>
          <div className="flex flex-1 flex-col items-center justify-center gap-5">
            {/* Compact crest carried over for continuity — the perks list is
                the focus of this act (UX Spec §2). */}
            <RankBlazon
              input={{
                name: pending.name,
                tier: pending.tier,
                tierCount: pending.tier_count,
                imageUrl: pending.image_url,
              }}
              size="crest"
              decorative
            />

            {/* The Privileges act still needs a DialogTitle for Radix a11y;
                the rank name remains the labelled title (visually hidden). */}
            <DialogTitle className="sr-only">
              {t("rank.modal.title_aria", { name: pending.name })}
            </DialogTitle>

            <span
              className="text-primary text-xs font-semibold tracking-[0.25em] uppercase"
              data-testid="rank-up-privileges-eyebrow"
            >
              {t("rank.modal.privileges_eyebrow")}
            </span>

            <ul
              data-testid="rank-up-privileges-list"
              className="flex w-full max-w-xs flex-col gap-3 text-left"
            >
              {pending.privileges.map((privilege, index) => {
                const Icon = PRIVILEGE_ICONS[index % PRIVILEGE_ICONS.length]
                return (
                  <li
                    key={privilege.id}
                    className={cn(
                      "bg-muted/40 flex items-center gap-3 rounded-xl px-4 py-3",
                      motionSafe("motion-safe:animate-rank-pop"),
                    )}
                    style={
                      reducedMotion
                        ? undefined
                        : { animationDelay: `${index * 60}ms` }
                    }
                  >
                    <span
                      aria-hidden="true"
                      className="bg-primary/10 text-primary flex size-9 shrink-0 items-center justify-center rounded-full"
                    >
                      <Icon className="size-4" />
                    </span>
                    <span className="text-sm font-medium text-balance">
                      {privilege.text}
                    </span>
                  </li>
                )
              })}
            </ul>
          </div>

          <div className="flex w-full flex-col gap-2">
            <Button
              data-testid="rank-up-privileges-continue"
              className="w-full"
              // Wrap so the click MouseEvent isn't passed as `onDismiss`'s
              // options arg (dismiss-in-place: no chained navigation).
              onClick={() => onDismiss()}
            >
              {t("rank.modal.cta_continue")}
            </Button>
          </div>
        </>
      )}
    </div>
  )
}
